import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6gt28au8ojn6g516k60', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', address: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          {/* Left - Contact Info */}
          <div>
            <div className="mb-6">
              <span className="text-teal-600 text-sm font-bold uppercase tracking-wider">Liên Hệ Ngay</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Gọi Ngay Để Được<br />
              <span className="text-teal-600">Báo Giá Miễn Phí</span>
            </h2>

            <p className="text-gray-600 text-base mb-8 leading-relaxed">
              Thu mua nhanh trong ngày - Thanh toán ngay tại chỗ<br />
              Đội ngũ chuyên nghiệp sẵn sàng phục vụ 24/7
            </p>

            {/* Contact Methods */}
            <div className="space-y-6 mb-8">
              <a href="tel:0968947136" className="flex items-center gap-4 group cursor-pointer">
                <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center group-hover:bg-teal-600 transition-colors">
                  <div className="w-14 h-14 flex items-center justify-center">
                    <i className="ri-phone-fill text-teal-600 group-hover:text-white text-2xl transition-colors"></i>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Hotline 24/7</div>
                  <div className="text-xl font-bold text-gray-900">0968.947.136</div>
                </div>
              </a>

              <a href="mailto:contact@namphat.vn" className="flex items-center gap-4 group cursor-pointer">
                <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center group-hover:bg-amber-500 transition-colors">
                  <div className="w-14 h-14 flex items-center justify-center">
                    <i className="ri-mail-fill text-amber-500 group-hover:text-white text-2xl transition-colors"></i>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Email</div>
                  <div className="text-lg font-bold text-gray-900">contact@namphat.vn</div>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <div className="w-14 h-14 flex items-center justify-center">
                    <i className="ri-map-pin-fill text-blue-600 text-2xl"></i>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Địa chỉ</div>
                  <div className="text-base font-bold text-gray-900">Khu công nghiệp, Việt Nam</div>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-3">
              <a href="#" className="w-12 h-12 bg-blue-600 hover:bg-blue-700 rounded-xl flex items-center justify-center transition-colors cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center">
                  <i className="ri-facebook-fill text-white text-xl"></i>
                </div>
              </a>
              <a href="#" className="w-12 h-12 bg-blue-500 hover:bg-blue-600 rounded-xl flex items-center justify-center transition-colors cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center">
                  <i className="ri-messenger-fill text-white text-xl"></i>
                </div>
              </a>
              <a href="https://zalo.me/0968947136" className="w-12 h-12 bg-blue-400 hover:bg-blue-500 rounded-xl flex items-center justify-center transition-colors cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center">
                  <i className="ri-chat-4-fill text-white text-xl"></i>
                </div>
              </a>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-8 shadow-xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Gửi Yêu Cầu Báo Giá</h3>
            
            <form onSubmit={handleSubmit} className="space-y-5" data-readdy-form id="contact-form">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Họ và tên *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Nhập họ tên của bạn"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Số điện thoại *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Nhập số điện thoại"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Địa chỉ *</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Nhập địa chỉ của bạn"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Loại phế liệu *</label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
                >
                  <option value="">Chọn loại phế liệu</option>
                  <option value="Sắt thép">Sắt thép</option>
                  <option value="Nhôm">Nhôm</option>
                  <option value="Đồng">Đồng</option>
                  <option value="Inox">Inox</option>
                  <option value="Máy móc cũ">Máy móc cũ</option>
                  <option value="Nhà xưởng thanh lý">Nhà xưởng thanh lý</option>
                  <option value="Khác">Khác</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ghi chú</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                  placeholder="Mô tả chi tiết về phế liệu cần thu mua..."
                ></textarea>
                <div className="text-xs text-gray-500 mt-1 text-right">{formData.message.length}/500</div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400 text-white py-4 rounded-xl text-sm font-bold transition-all whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Đang gửi...
                  </>
                ) : (
                  <>
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-send-plane-fill text-lg"></i>
                    </div>
                    GỬI YÊU CẦU
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm">
                  <strong>Thành công!</strong> Chúng tôi sẽ liên hệ với bạn sớm nhất.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <strong>Lỗi!</strong> Vui lòng thử lại hoặc gọi hotline.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}